'use client';

import Image from 'next/image';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import {
  BookOpen,
  Clock,
  Gift,
  Shield,
  CheckCircle,
  Play,
  AlertCircle,
  Info,
  ChevronRight,
  ArrowRight,
  HelpCircle,
  ShoppingCart,
  Shuffle,
  FileText,
  RotateCcw,
} from 'lucide-react';
import { SolidNavbar } from '@/components/SolidNavbar';
import { Footer } from '@/components/Footer';
import { ShareButton } from '@/components/ShareButton';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { useOwnedItems } from '@/context/OwnedItemsContext';
import { useProductViewTracking } from '@/hooks/useProductViewTracking';
import { usePaymentMethods } from '@/hooks/usePaymentMethods';
import { QrisTeaser } from '@/components/payment/QrisTeaser';
import { PAGE_CONTAINER } from '@/components/ui';

interface Test {
  id: string;
  title: string;
  durationMins: number;
}

interface ModuleDetail {
  id: string;
  title: string;
  slug: string;
  description: string;
  // Short student-facing notice (e.g. TOEFL "bilingual explanations" disclaimer).
  note?: string | null;
  coverImageUrl: string | null;
  price: number;
  basePrice: number;
  promoPrice: number | null;
  isFree: boolean;
  isPublished: boolean;
  isRandomized: boolean;
  showExplanations: string;
  maxAttempts: number | null;
  antiCheatMode: string;
  antiCheatMaxWarnings: number;
  antiCheatAutoSubmit: boolean;
  // Latihan tier signal (MINI/PARTISI/FULL) — nullable/absent, never assume a default.
  reportType?: 'FULL_EXAM' | 'SECTION_DRILL' | 'QUICK_PRACTICE' | null;
  tests: Test[];
  examTypes?: { id: string; name: string; slug: string; color: string | null }[];
}

interface RelatedModule {
  id: string;
  slug: string;
  title: string;
  coverImageUrl: string | null;
  price: number;
  isFree: boolean;
  tests: { id: string }[];
  examTypes?: { color: string | null }[];
}

const ANTI_CHEAT_KEYS = ['LENIENT', 'MODERATE', 'STRICT'] as const;
const EXPLANATION_KEYS = ['AFTER_SUBMIT', 'DURING_TEST', 'NEVER'] as const;

// Latihan tier signal (P-02) — derives the MINI/PARTISI/FULL badge straight from
// `reportType`. No parallel tier enum: two near-identical enums would drift apart.
const TIER_LABEL_KEY: Record<string, 'tierMini' | 'tierPartisi' | 'tierFull'> = {
  QUICK_PRACTICE: 'tierMini',
  SECTION_DRILL: 'tierPartisi',
  FULL_EXAM: 'tierFull',
};

function formatPrice(price: number) {
  return `Rp ${price.toLocaleString('id-ID')}`;
}

function LoadingSkeleton() {
  const t = useTranslations('moduleDetail');
  return (
    <>
      <SolidNavbar breadcrumbs={[{ label: t('loading'), href: '#' }]} />
      <main className="bg-slate-50 pb-24 lg:pb-12">
        <div className={`${PAGE_CONTAINER} py-4`}>
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              {/* Mobile cover skeleton */}
              <div className="lg:hidden aspect-video rounded-xl shimmer" />
              {/* Title card skeleton */}
              <div className="bg-white rounded-xl p-5 border border-slate-200 space-y-3">
                <div className="h-5 w-16 shimmer rounded-md" />
                <div className="h-7 w-3/4 shimmer rounded-md" />
                <div className="h-4 w-full shimmer rounded-md" />
                <div className="h-4 w-2/3 shimmer rounded-md" />
                <div className="flex gap-6 pt-4 border-t border-slate-100">
                  <div className="h-4 w-20 shimmer rounded" />
                  <div className="h-4 w-24 shimmer rounded" />
                  <div className="h-4 w-20 shimmer rounded" />
                </div>
              </div>
              {/* Tests skeleton */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-100">
                  <div className="h-5 w-28 shimmer rounded" />
                </div>
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="px-5 py-3 flex items-center gap-3 border-b border-slate-100 last:border-0">
                    <div className="w-6 h-6 shimmer rounded" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-4 w-3/4 shimmer rounded" />
                      <div className="h-3 w-16 shimmer rounded" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-1 space-y-4">
              <div className="hidden lg:block aspect-video rounded-xl shimmer" />
              <div className="bg-white rounded-xl p-5 border border-slate-200 space-y-3">
                <div className="h-8 w-24 shimmer rounded mx-auto" />
                <div className="h-11 shimmer rounded-lg" />
                <div className="space-y-2 pt-2">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-4 h-4 shimmer rounded-full" />
                      <div className="h-3 w-40 shimmer rounded" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

export function ModuleDetailClient() {
  const { isAuthenticated } = useAuth();
  const { addItem, openDrawer, isInCart } = useCart();
  const { methods: paymentMethods } = usePaymentMethods();
  const qrisMethod = paymentMethods.find((m) => m.category === 'QRIS') ?? null;
  const { ownedModuleIds } = useOwnedItems();
  const params = useParams();
  const slug = params.slug as string;
  const searchParams = useSearchParams();
  // "?src=after-free-tryout" (or similar) — a test-engine result/pembahasan CTA
  // attribution tag, threaded into product.viewed's metadata.source below.
  const viewSource = searchParams.get('src') ?? undefined;

  const [module, setModule] = useState<ModuleDetail | null>(null);
  const [relatedModules, setRelatedModules] = useState<RelatedModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [showAllTests, setShowAllTests] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const moduleRes = await fetch(`/api/proxy/test/modules/slug/${slug}`);
        if (!moduleRes.ok) throw new Error('Failed to fetch module');
        const moduleData = await moduleRes.json();
        setModule(moduleData);

        const allRes = await fetch(`/api/proxy/test/modules`);
        if (allRes.ok) {
          const allData = await allRes.json();
          const filtered = allData
            .filter((m: RelatedModule & { slug: string }) => m.slug !== slug)
            .slice(0, 3);
          setRelatedModules(filtered);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load module');
      } finally {
        setLoading(false);
      }
    };

    if (slug) fetchData();
  }, [slug]);

  const t = useTranslations('moduleDetail');

  // Called unconditionally, ahead of the loading/not-found early returns below (Rules of
  // Hooks) — no-ops internally until module?.id resolves (see the hook's own guard).
  useProductViewTracking('TEST_MODULE', module?.id, module?.promoPrice ?? module?.price, viewSource);

  if (loading) return <LoadingSkeleton />;

  if (error || !module) {
    return (
      <>
        <SolidNavbar breadcrumbs={[{ label: 'Error', href: '#' }]} />
        <main className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
          <div className="w-full max-w-sm">
            <div className="h-1 rounded-t-2xl bg-teal-600" />
            <div className="bg-white rounded-b-2xl border border-slate-200 border-t-0 px-8 py-10 text-center shadow-sm">
              <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-5">
                <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
              </div>
              <p className="font-mono text-xs tracking-[0.15em] uppercase text-slate-400 mb-1">{t('error')}</p>
              <p className="text-slate-800 font-semibold text-base mb-2">{t('gagalMemuat')}</p>
              <p className="text-slate-500 text-xs leading-relaxed mb-8">
                {t('gagalMuatDesc')}
              </p>
              <Link
                href="/"
                className="block w-full py-3 rounded-xl font-semibold text-sm text-white text-center bg-teal-600 hover:bg-teal-700 transition-colors"
              >
                {t('kembaliKeBeranda')}
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const totalDuration = module.tests?.reduce((acc, t) => acc + (t.durationMins || 0), 0) || 0;
  const dashboardUrl = process.env.NEXT_PUBLIC_DASHBOARD_URL || 'http://localhost:3502';
  const dashboardModuleUrl = `${dashboardUrl}/tests/${module.slug || module.id}`;

  const hasPromo = !module.isFree && module.promoPrice !== null && module.promoPrice !== undefined
    && module.basePrice && module.promoPrice < module.basePrice;
  const discount = hasPromo
    ? Math.round(((module.basePrice - module.promoPrice!) / module.basePrice) * 100)
    : 0;

  const accentColor = module.examTypes?.[0]?.color ?? '#0d9488';
  const tierLabelKey = module.reportType ? TIER_LABEL_KEY[module.reportType] : undefined;

  const breadcrumbs = [
    { label: t('breadcrumbTests'), href: '/#tests' },
    { label: module.title, href: `/tests/${module.slug || module.id}` }
  ];

  const ANTI_CHEAT_LABELS: Record<string, { label: string; desc: string }> = Object.fromEntries(
    ANTI_CHEAT_KEYS.map(k => [k, {
      label: t(`antiCheatLabels.${k}` as Parameters<typeof t>[0]),
      desc: t(`antiCheatDescs.${k}` as Parameters<typeof t>[0]),
    }])
  );
  const EXPLANATION_LABELS: Record<string, { label: string; desc: string }> = Object.fromEntries(
    EXPLANATION_KEYS.map(k => [k, {
      label: t(`explanationLabels.${k}` as Parameters<typeof t>[0]),
      desc: t(`explanationDescs.${k}` as Parameters<typeof t>[0]),
    }])
  );
  const faqs = t.raw('faqs') as { q: string; a: string }[];

  const isOwned = ownedModuleIds.has(module.id);
  const inCart = isInCart(module.id);

  const handleAddToCart = () => {
    if (isOwned) {
      window.location.href = dashboardModuleUrl;
      return;
    }
    if (inCart) {
      openDrawer();
      return;
    }
    addItem({
      type: 'module',
      moduleId: module.id,
      title: module.title,
      price: module.price,
      basePrice: module.basePrice,
      promoPrice: module.promoPrice,
      isFree: module.isFree,
      coverImageUrl: module.coverImageUrl,
    });
    openDrawer();
  };

  // CTA label based on state
  const ctaLabel = isOwned
    ? t('ctaBukaDir')
    : inCart
      ? t('ctaSudahDiKeranjang')
      : !isAuthenticated && module.isFree
        ? t('ctaDaftarMulai')
        : t('ctaTambahKeranjang');

  const CtaIcon = isOwned ? Play : inCart ? CheckCircle : ShoppingCart;

  return (
    <>
      <SolidNavbar breadcrumbs={breadcrumbs} />
      {/* pb clears the fixed sticky CTA bar below (~96px baseline) plus the
          iOS home-indicator safe area — keep the baseline in sync with the
          bar's own py-3 + content height. */}
      <main className="bg-slate-50 pb-[calc(6rem+env(safe-area-inset-bottom))] lg:pb-12">
        <div className={`${PAGE_CONTAINER} py-4`}>
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-4 min-w-0">
              {/* Title Card */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                {/* Cover image — full width at top */}
                <div className="relative h-44 sm:h-52 bg-slate-100">
                  <Image src={module.coverImageUrl || `/tests/${slug}/opengraph-image`} alt={module.title} fill sizes="(max-width: 1024px) 100vw, 66vw" className="object-cover" />
                </div>

                {/* Text below image */}
                <div className="p-5">
                  {/* Badges row */}
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    {/* Only FREE gets a badge — "Premium" on every paid module was noise;
                        the price panel below already says what it costs. */}
                    {module.isFree && (
                      <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-600 px-2.5 py-0.5 rounded-md text-xs font-semibold border border-emerald-100">
                        <Gift className="w-3 h-3" />
                        {t('gratisLabel')}
                      </span>
                    )}
                    {/* Tier badge — degrades silently when reportType is null/absent. */}
                    {tierLabelKey && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold border bg-blue-50 text-blue-600 border-blue-100">
                        {t(tierLabelKey)}
                      </span>
                    )}
                    {module.examTypes?.map((et) => (
                      <span
                        key={et.id}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold border"
                        style={{
                          backgroundColor: `${et.color ?? accentColor}15`,
                          color: et.color ?? accentColor,
                          borderColor: `${et.color ?? accentColor}30`,
                        }}
                      >
                        {et.name}
                      </span>
                    ))}
                  </div>

                  <h1 className="text-2xl sm:text-3xl font-bold leading-tight text-slate-900 mb-1">{module.title}</h1>
                  <p className="text-sm text-slate-600">{module.description}</p>

                  {module.note && (
                    <div className="mt-3 flex items-start gap-2 bg-sky-50 border border-sky-100 text-sky-900 rounded-lg p-3 text-sm">
                      <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-sky-600" />
                      <p>{module.note}</p>
                    </div>
                  )}

                  {/* Stats row */}
                  <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4 pt-4 border-t border-slate-100">
                    <div className="flex items-center gap-1.5 text-sm text-slate-600">
                      <BookOpen className="w-4 h-4 text-teal-600 flex-shrink-0" />
                      {t('ujian', { count: String(module.tests?.length || 0) })}
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-slate-600">
                      <Clock className="w-4 h-4 text-orange-500 flex-shrink-0" />
                      {t('menitTotal', { menit: String(totalDuration) })}
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-slate-600">
                      <RotateCcw className="w-4 h-4 text-purple-500 flex-shrink-0" />
                      {module.maxAttempts
                        ? t('maxPercobaan', { count: String(module.maxAttempts) })
                        : t('unlimitedPercobaan')
                      }
                    </div>
                    {module.showExplanations !== 'NEVER' && (
                      <div className="flex items-center gap-1.5 text-sm text-slate-600">
                        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        <span>{t('pembahasanTersedia')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Tests List */}
              <div className="bg-white rounded-xl border border-slate-200">
                <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                  <h2 className="font-semibold text-slate-900">{t('pilihanUjian')}</h2>
                  <span className="text-xs text-slate-400">{t('paketCount', { count: String(module.tests?.length || 0) })}</span>
                </div>
                <div className="divide-y divide-slate-100">
                  {module.tests?.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-6">{t('belumAdaUjian')}</p>
                  ) : (
                    (showAllTests ? module.tests : module.tests?.slice(0, 5))?.map((test, index) => (
                      <div key={test.id} className="px-5 py-3.5 flex items-center gap-3 hover:bg-slate-50 transition-colors">
                        <span className="w-7 h-7 bg-slate-100 rounded-lg text-xs font-bold flex items-center justify-center text-slate-500 flex-shrink-0">
                          {index + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-slate-900 truncate">{test.title}</p>
                          <div className="flex items-center gap-1 mt-0.5">
                            <Clock className="w-3 h-3 text-slate-400" />
                            <span className="text-sm text-slate-500">{test.durationMins} menit</span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                  {(module.tests?.length ?? 0) > 5 && (
                    <button
                      onClick={() => setShowAllTests(v => !v)}
                      className="w-full text-center text-sm text-teal-600 hover:text-teal-700 py-3 transition-colors font-medium"
                    >
                      {showAllTests
                        ? t('tampilkanLebihSedikit')
                        : t('lihatUjianLainnya', { count: String((module.tests?.length ?? 0) - 5) })}
                    </button>
                  )}
                </div>
              </div>

              {/* Features */}
              <div className="bg-white rounded-xl p-5 border border-slate-200">
                <h2 className="font-semibold text-slate-900 mb-3">{t('detailFitur')}</h2>
                <div className="grid grid-cols-2 gap-3">
                  <FeatureTile
                    icon={FileText}
                    label={t('pembahasan')}
                    value={EXPLANATION_LABELS[module.showExplanations]?.label || module.showExplanations || '—'}
                    desc={EXPLANATION_LABELS[module.showExplanations]?.desc}
                    accent={module.showExplanations !== 'NEVER' ? 'teal' : 'slate'}
                  />
                  <FeatureTile
                    icon={Shuffle}
                    label={t('urutanSoal')}
                    value={module.isRandomized ? t('diacak') : t('tetap')}
                    desc={module.isRandomized ? t('diacakDesc') : t('tetapDesc')}
                    accent={module.isRandomized ? 'purple' : 'slate'}
                  />
                  <FeatureTile
                    icon={Shield}
                    label={t('antiCheat')}
                    value={ANTI_CHEAT_LABELS[module.antiCheatMode]?.label || module.antiCheatMode || '—'}
                    desc={ANTI_CHEAT_LABELS[module.antiCheatMode]?.desc}
                    accent={module.antiCheatMode === 'STRICT' ? 'red' : module.antiCheatMode === 'MODERATE' ? 'amber' : 'slate'}
                  />
                  <FeatureTile
                    icon={RotateCcw}
                    label={t('percobaanLabel')}
                    value={module.maxAttempts ? `${module.maxAttempts}x` : t('tidakTerbatas')}
                    desc={module.maxAttempts
                      ? t('antiCheatDesc', { max: String(module.antiCheatMaxWarnings), autoSubmit: module.antiCheatAutoSubmit ? t('antiCheatAutoSubmit') : t('antiCheatNoAutoSubmit') })
                      : undefined}
                    accent="blue"
                  />
                </div>
                {module.antiCheatMode !== 'LENIENT' && (
                  <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-100 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-700">
                      {t('antiCheatDesc', { max: String(module.antiCheatMaxWarnings), autoSubmit: module.antiCheatAutoSubmit ? t('antiCheatAutoSubmit') : t('antiCheatNoAutoSubmit') })}
                    </p>
                  </div>
                )}
              </div>

              {/* FAQ Section */}
              <div className="bg-white rounded-xl border border-slate-200">
                <div className="px-5 py-3 border-b border-slate-100">
                  <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-teal-600" />
                    {t('pertanyaanUmum')}
                  </h2>
                </div>
                <div className="divide-y divide-slate-100">
                  {faqs.map((faq, index) => (
                    <div key={index}>
                      <button
                        onClick={() => setOpenFaq(openFaq === index ? null : index)}
                        className="w-full px-5 py-3 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                      >
                        <span className="text-sm font-medium text-slate-900">{faq.q}</span>
                        <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-90' : ''}`} />
                      </button>
                      {openFaq === index && (
                        <div className="px-5 pb-3">
                          <p className="text-sm text-slate-600">{faq.a}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>


            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 min-w-0">
              <div className="space-y-4 lg:sticky lg:top-24">
                {/* CTA Card */}
                <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
                  {/* Price / Owned state */}
                  {isOwned ? (
                    <div className="mb-4">
                      <div className="flex items-center gap-2 text-teal-600">
                        <CheckCircle className="w-5 h-5" />
                        <span className="text-lg font-bold">{t('owned')}</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{t('aksesSelamanya')}</p>
                    </div>
                  ) : (
                    <div className="mb-4">
                      {module.isFree ? (
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl font-bold text-emerald-600">{t('gratis')}</span>
                          <span className="text-xs text-slate-400">{t('aksesSelamanySuffix')}</span>
                        </div>
                      ) : hasPromo ? (
                        <div>
                          <div className="flex items-baseline gap-2">
                            <span className="text-2xl font-bold text-orange-600">{formatPrice(module.promoPrice!)}</span>
                            <span className="bg-orange-100 text-orange-600 text-xs font-bold px-1.5 py-0.5 rounded">-{discount}%</span>
                          </div>
                          <span className="text-sm text-slate-400 line-through">{formatPrice(module.basePrice)}</span>
                          <p className="text-xs text-slate-400 mt-0.5">{t('aksesSelamanya')}</p>
                          <QrisTeaser price={module.promoPrice!} qrisMethod={qrisMethod} className="mt-1" />
                        </div>
                      ) : (
                        <div>
                          <span className="text-2xl font-bold text-slate-900">{formatPrice(module.price)}</span>
                          <p className="text-xs text-slate-400 mt-0.5">{t('aksesSelamanya')}</p>
                          <QrisTeaser price={module.price} qrisMethod={qrisMethod} className="mt-1" />
                        </div>
                      )}
                    </div>
                  )}

                  {/* CTA Button */}
                  {module.tests?.length > 0 && (
                    <button
                      onClick={handleAddToCart}
                      className={`w-full text-center py-3 rounded-xl font-semibold text-sm transition-colors mb-4 flex items-center justify-center gap-1.5 ${
                        isOwned
                          ? 'bg-teal-600 hover:bg-teal-700 text-white'
                          : inCart
                            ? 'bg-slate-100 text-slate-600 cursor-default'
                            : 'bg-orange-500 hover:bg-orange-600 text-white'
                      }`}
                    >
                      <CtaIcon className="w-4 h-4" />
                      {ctaLabel}
                    </button>
                  )}

                  {/* Included checklist */}
                  <div className="border-t border-slate-100 pt-3 space-y-2">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{t('termasukDalamPaket')}</p>
                    <SidebarCheckItem text={t('includeUjian', { count: String(module.tests?.length || 0) })} />
                    <SidebarCheckItem text={t('includeDurasi', { dur: String(totalDuration) })} />
                    {module.showExplanations !== 'NEVER' && (
                      <SidebarCheckItem text={t('includePembahasan')} />
                    )}
                    <SidebarCheckItem
                      text={module.maxAttempts ? t('includePercobaan', { max: String(module.maxAttempts) }) : t('includeUnlimitedPercobaan')}
                    />
                    <SidebarCheckItem text={t('includeSkor')} />
                    <SidebarCheckItem text={t('includeAkses')} />
                  </div>

                  <div className="pt-3 border-t border-slate-100">
                    <ShareButton
                      title={module.title}
                      text={t('shareText', { title: module.title })}
                      variant="prominent"
                    />
                  </div>
                </div>

                {/* Related Packages */}
                {relatedModules.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200">
                    <div className="px-4 py-3 border-b border-slate-100">
                      <h3 className="font-semibold text-slate-900 text-sm">{t('paketLainnya')}</h3>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {relatedModules.map((related) => {
                        const relatedAccent = related.examTypes?.[0]?.color ?? '#0d9488';
                        return (
                          <Link
                            key={related.id}
                            href={`/tests/${related.slug || related.id}`}
                            className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                          >
                            <div className="relative w-14 h-10 rounded-lg overflow-hidden shrink-0 bg-slate-100">
                              <Image src={related.coverImageUrl || `/tests/${related.slug}/opengraph-image`} alt={related.title} fill sizes="56px" className="object-cover" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-slate-900 line-clamp-1">{related.title}</p>
                              <p className="text-xs text-slate-500">{t('ujianCount', { count: String(related.tests?.length || 0) })}</p>
                            </div>
                            <span className="text-sm font-semibold shrink-0 ml-1">
                              {related.isFree ? (
                                <span className="text-emerald-600">{t('gratis')}</span>
                              ) : (
                                <span className="text-slate-900">{formatPrice(related.price)}</span>
                              )}
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                    <div className="px-4 py-2.5 border-t border-slate-100">
                      <Link
                        href="/tests"
                        className="text-xs font-medium text-teal-600 hover:text-teal-700 flex items-center justify-center gap-1"
                      >
                        {t('lihatSemuaPaket')}
                        <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                )}

                {/* Help Card */}
                <div className="bg-slate-900 rounded-xl p-4 text-white">
                  <h3 className="font-semibold text-sm mb-1">{t('butuhBantuan')}</h3>
                  <p className="text-xs text-slate-400 mb-3">{t('teamSupport')}</p>
                  <a
                    href="mailto:support@aksademia.com"
                    className="text-xs text-teal-400 hover:text-teal-300 underline"
                  >
                    {t('hubungiSupport')}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom CTA Banner */}
          <div className="bg-pattern overflow-hidden mt-8 bg-teal-700 rounded-xl p-5 text-white">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="font-bold text-lg">{t('siapMeningkatkanSkor')}</h3>
                <p className="text-teal-50 text-sm">{t('siapDesc')}</p>
              </div>
              <button
                onClick={handleAddToCart}
                className="inline-flex items-center justify-center gap-2 bg-white text-teal-700 px-6 py-2.5 rounded-lg font-bold text-sm hover:bg-teal-50 transition-colors"
              >
                {ctaLabel}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Sticky bottom CTA — mobile only */}
      <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-white border-t border-slate-200 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] flex items-center gap-3">
        <div className="flex-1 min-w-0">
          {isOwned ? (
            <>
              <p className="text-sm font-bold text-teal-600">{t('dimiliki')}</p>
              <p className="text-xs text-slate-400">{t('aksesSelamanya')}</p>
            </>
          ) : module.isFree ? (
            <>
              <p className="text-sm font-bold text-emerald-600">{t('gratis')}</p>
              <p className="text-xs text-slate-400">{t('aksesSelamanya')}</p>
            </>
          ) : hasPromo ? (
            <>
              <div className="flex items-baseline gap-1.5">
                <p className="text-sm font-bold text-orange-600">{formatPrice(module.promoPrice!)}</p>
                <p className="text-xs text-slate-400 line-through">{formatPrice(module.basePrice)}</p>
              </div>
              <p className="text-xs text-slate-400">{t('aksesSelamanya')}</p>
            </>
          ) : (
            <>
              <p className="text-sm font-bold text-slate-900">{formatPrice(module.price)}</p>
              <p className="text-xs text-slate-400">{t('aksesSelamanya')}</p>
            </>
          )}
        </div>
        <button
          onClick={handleAddToCart}
          className={`flex-shrink-0 px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors ${
            isOwned ? 'bg-teal-600 hover:bg-teal-700 text-white' : inCart ? 'bg-slate-200 text-slate-600' : 'bg-orange-500 hover:bg-orange-600 text-white'
          }`}
        >
          {isOwned ? t('bukaDashboard') : inCart ? t('diKeranjang') : module.isFree ? t('ikutiGratis') : t('ikutiLatihan')}
        </button>
      </div>

      <Footer />
    </>
  );
}

type AccentColor = 'teal' | 'purple' | 'amber' | 'red' | 'blue' | 'slate';

function FeatureTile({
  icon: Icon,
  label,
  value,
  desc,
  accent,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  desc?: string;
  accent: AccentColor;
}) {
  const accentClasses: Record<AccentColor, string> = {
    teal: 'bg-teal-50 text-teal-600',
    purple: 'bg-purple-50 text-purple-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
    blue: 'bg-blue-50 text-blue-600',
    slate: 'bg-slate-100 text-slate-500',
  };

  return (
    <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${accentClasses[accent]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-slate-500 leading-tight">{label}</p>
        <p className="text-sm font-semibold text-slate-800 leading-tight mt-0.5">{value}</p>
        {desc && <p className="text-sm text-slate-400 leading-snug mt-0.5">{desc}</p>}
      </div>
    </div>
  );
}

function SidebarCheckItem({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2">
      <CheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0" />
      <span className="text-sm text-slate-600">{text}</span>
    </div>
  );
}
